"use client";

import { Plus } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function BookingFaq() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: faqRef,
    isVisible: faqVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const faqs = [{
    id: "Q01",
    question: "¿Te desplazas para sesiones en exteriores?",
    answer: "Sí. Estoy disponible para sesiones de modelos profesionales y proyectos editoriales en Valencia y Madrid. Los gastos de viaje se calculan en función de la ubicación y los requisitos del proyecto. Por favor, ponte en contacto para hablar de tus necesidades específicas."
  }, {
    id: "Q02",
    question: "¿Qué incluye una sesión de peluquería y maquillaje?",
    answer: "Cada sesión incluye una preparación profesional de la piel, aplicación de maquillaje de alta gama adaptada a tus rasgos y peinado. Utilizo productos de primera calidad para garantizar un acabado impecable y duradero, adecuado para fotografía de alta resolución."
  }, {
    id: "Q03",
    question: "¿Cómo debo prepararme para mi cita?",
    answer: "Por favor, ven con la cara limpia e hidratada y el pelo limpio y seco. Si tienes fotos de inspiración específicas o un mood board para la sesión, compártelos conmigo al menos 48 horas antes de nuestra sesión."
  }, {
    id: "Q04",
    question: "¿Aceptas reservas para grupos?",
    answer: "Sí, puedo acomodar reservas de grupos para equipos editoriales o sesiones comerciales. Para grupos más grandes, puedo traer a un asistente para asegurar que cumplimos con el horario manteniendo los más altos estándares de calidad."
  }, {
    id: "Q05",
    question: "¿Ofreces sesiones de prueba?",
    answer: "Las sesiones de prueba están disponibles para novias o conceptos editoriales complejos. Estas sesiones nos permiten perfeccionar el look y asegurar que todo esté perfecto para tu gran día o proyecto importante."
  }, {
    id: "Q06",
    question: "¿Cuál es tu política de cancelación?",
    answer: "Las citas pueden reprogramarse o cancelarse con al menos 72 horas de antelación. Las cancelaciones realizadas dentro de las 48 horas previas a la cita pueden estar sujetas a una tasa de reserva para cubrir el tiempo reservado."
  }, {
    id: "Q07",
    question: "¿Trabajas con tipos de piel específicos?",
    answer: "Tengo experiencia trabajando con todos los tipos y tonos de piel. Mi kit está seleccionado para proporcionar resultados inclusivos y de alto rendimiento que luzcan naturales y radiantes bajo la iluminación del estudio."
  }, {
    id: "Q08",
    question: "¿Cómo aseguro mi reserva?",
    answer: "Todas las reservas se confirman tras la recepción de un depósito. Puedes asegurar tu fecha contactándome por correo electrónico o por mensaje directo en Instagram. Recomiendo reservar con al menos 2-4 semanas de antelación para fechas de temporada alta."
  }, {
    id: "Q09",
    question: "¿Puedo ver tus certificaciones?",
    answer: "Por supuesto. Soy maquilladora profesional certificada con amplia formación en belleza editorial y nupcial. Estaré encantada de proporcionarte mis credenciales y referencias de mi portafolio si lo solicitas."
  }];
  return <section data-section-id="3935" id="booking-faq" className="bg-background text-foreground relative overflow-hidden py-20 lg:py-28">
      {}
      <div className="absolute top-8 left-4 lg:left-8 font-mono text-[10px] tracking-widest text-muted-foreground/60">
        // PREGUNTAS FRECUENTES — SERVICIOS DE BELLEZA
      </div>
      <div className="absolute top-8 right-4 lg:right-8 font-mono text-[10px] tracking-widest text-muted-foreground/60">
        SEC_07 / SOPORTE
      </div>

      {}
      <div className="absolute top-16 right-4 lg:right-8 flex items-center gap-1 font-mono text-[10px] text-primary/70">
        <span>+</span>
        <span>39.4699°N / 0.3763°W</span>
      </div>

      <div className="container mx-auto px-4 lg:px-8 mt-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          {}
          <div ref={headerRef} className={`lg:col-span-4 relative transition-all duration-700 ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="lg:sticky lg:top-24 relative pr-6">
              <div className="font-mono text-[11px] tracking-[0.25em] text-primary mb-6">
                // PREGUNTAS FRECUENTES
              </div>

              <h2 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight text-foreground mb-6">
                Tus
                <br />
                Preguntas de
                <br />
                Belleza.
              </h2>

              <p className="font-default text-base text-muted-foreground leading-relaxed mb-8 max-w-md">
                Respuestas a preguntas comunes sobre mis servicios de peluquería y maquillaje. Si necesitas más ayuda, siempre estoy disponible para una consulta personal.
              </p>

              <Link to="/ContactPage">
                <Button variant="ghost" className="font-mono text-xs tracking-widest uppercase border border-border hover:border-primary hover:bg-transparent hover:text-primary px-6 h-11">
                  Ponte en Contacto →
                </Button>
              </Link>

              {}
              <div className="hidden lg:block absolute top-0 right-0 h-full w-px" style={{
              backgroundImage: "linear-gradient(to bottom, var(--border) 50%, transparent 50%)",
              backgroundSize: "1px 8px"
            }} aria-hidden="true" />
              <div className="hidden lg:flex absolute top-1/2 right-0 -translate-y-1/2 translate-x-1/2 items-center justify-center w-3 h-3 text-primary">
                <span className="font-mono text-base leading-none">+</span>
              </div>
              <div className="hidden lg:flex absolute top-0 right-0 -translate-y-1/2 translate-x-1/2 items-center justify-center w-3 h-3 text-primary/60">
                <span className="font-mono text-xs leading-none">+</span>
              </div>
              <div className="hidden lg:flex absolute bottom-0 right-0 translate-y-1/2 translate-x-1/2 items-center justify-center w-3 h-3 text-primary/60">
                <span className="font-mono text-xs leading-none">+</span>
              </div>
            </div>
          </div>

          {}
          <div className="hidden lg:block lg:col-span-1" aria-hidden="true" />

          {}
          <div ref={faqRef} className={`lg:col-span-7 transition-all duration-700 delay-150 ${faqVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, i) => <AccordionItem data-index={i} key={faq.id} value={faq.id} className="border-0 group relative">
                  {}
                  <div className="absolute top-0 left-0 right-0 h-px" style={{
                backgroundImage: "linear-gradient(to right, var(--border) 50%, transparent 50%)",
                backgroundSize: "8px 1px"
              }} aria-hidden="true" />

                  {}
                  <div className="absolute left-0 top-0 bottom-0 w-px bg-primary opacity-0 group-data-[state=open]:opacity-100 transition-opacity duration-300" />

                  <AccordionTrigger className="hover:no-underline py-6 pl-4 pr-2 group-data-[state=open]:pl-6 transition-all [&>svg]:hidden">
                    <div className="flex items-start gap-4 sm:gap-6 flex-1 text-left">
                      <span className="font-mono text-xs sm:text-sm text-muted-foreground/70 tracking-wider pt-1 shrink-0">
                        {faq.id}
                      </span>
                      <span className="font-default text-base sm:text-lg font-semibold text-foreground leading-snug group-data-[state=open]:text-primary transition-colors">
                        {faq.question}
                      </span>
                    </div>
                    <div className="shrink-0 ml-4 flex items-center justify-center w-8 h-8">
                      <Plus className="size-5 text-primary transition-transform duration-300 group-data-[state=open]:rotate-45" />
                    </div>
                  </AccordionTrigger>

                  <AccordionContent className="pl-4 sm:pl-[calc(2rem+2.5rem)] pr-4 pb-6 group-data-[state=open]:pl-6 sm:group-data-[state=open]:pl-[calc(2.5rem+2.5rem)]">
                    <p className="font-default text-sm sm:text-base text-muted-foreground leading-relaxed max-w-2xl">
                      {faq.answer}
                    </p>
                  </AccordionContent>
                </AccordionItem>)}

              {}
              <div className="h-px w-full" style={{
              backgroundImage: "linear-gradient(to right, var(--border) 50%, transparent 50%)",
              backgroundSize: "8px 1px"
            }} aria-hidden="true" />
            </Accordion>

            {}
            <div className="mt-8 flex items-start gap-2 font-mono text-[11px] tracking-widest text-muted-foreground uppercase">
              <span className="text-primary">//</span>
              <span>
                ¿AÚN TIENES PREGUNTAS? EMAIL —{" "}
                <Link to="mailto:hello@nikibmakeup.com" className="text-foreground hover:text-primary transition-colors underline underline-offset-4 decoration-dashed">nikibarrios7@gmail.com</Link>
              </span>
            </div>
          </div>
        </div>
      </div>

      {}
      <div className="container mx-auto px-4 lg:px-8 mt-16">
        <div className="flex items-center justify-between font-mono text-[10px] tracking-widest text-muted-foreground/50">
          <span><br /></span>
          <span>// NIKI B. MAKEUP</span>
        </div>
      </div>
    </section>;
}